/**
 * Export pe3d variants: which variants on the left, which networks and how on
 * the right → one zip on S3.
 *
 * One dialog for one variant, a folder or a whole base: it opens with the
 * variants it was asked for ticked (`initialIDs`), their folder open and the
 * others shut, and any variant can be added, by row, by folder or all at once. Several variants come out side by side in
 * one zip, each in a folder of its own. The networks offered are the ones
 * every ticked variant's release was built for. The loading screen is NOT
 * chosen here: it comes from the game, whose release names it (the server's
 * makeCreatives reads the bundle), and a release that names none falls back to
 * the company's setting.
 *
 * A large export is a few minutes of work on the server, so what it is doing
 * is shown as it goes, and when some variants could not be made the dialog
 * stays open and says which and why — the zip of the rest is still delivered.
 *
 * Plan usage is counted here, like the other export dialogs: one playable per
 * network per variant that came back exported.
 *
 * Naming is NOT chosen here: the company's convention names what comes out,
 * and it is edited once in Account settings → Naming Convention. The dialog
 * only shows the name the convention produces.
 *
 * Laid out like New variant (dialogPe3dNewVariant.js): two columns of
 * checklists in the snapshot editor's dialog chrome (pe3dPanelKit).
 */

import React, { useEffect, useMemo, useRef, useState } from "react";
import _ from "lodash";
import {
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Input,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  Select,
  Typography,
} from "@material-ui/core";
import { ThemeProvider, createTheme } from "@material-ui/core/styles";
import { CONTROL_BG, CONTROL_BORDER } from "../snapshotEditor/panelPalette";
import { exportVariants, fetchExportNaming, variantSizeEstimate } from "../../modules/pe3dApi";
import { formatBytes } from "../../modules/playableSizeEstimate";
import DownloadIcon from "@mui/icons-material/Download";
import { exportFileName, folderNames } from "../../modules/pe3dExportNaming";
import ExportPickerList, { idsUnder } from "../exportPickerList";
import { deliversToDropbox, dropboxDestination, templateUsesGameTitle } from "../../modules/buildProfileUtils";
import DialogDropboxSubmit from "../dialogDropboxSubmit";
import { incrementCompanyUsage } from "../../modules/companySubscriptionApi";
import { networkCaption, networkViews } from "../../modules/playEngineExportTargets";
import { LANGUAGE_CAPTIONS } from "../../modules/pe3dLocalization";
import { buildVariantTree, variantGroups } from "../../modules/pe3dVariantTree";
import { DIALOG_CAPTION, DIALOG_PAPER_PROPS, DIALOG_TEXT, MUTED, TEXT } from "./pe3dPanelKit";

// The server's own limits (controller/pe3d/exportVariants.js), checked here so the answer is not a refusal.
const MAX_VARIANTS = 300;
const MAX_PLAYABLES = 1000;

// A fixed height rather than one the content decides: the right-hand column
// is what makes this dialog tall, and the list has to fill whatever that comes
// to instead of stopping at its own size.
const PAPER_PROPS = {
  style: {
    ...DIALOG_PAPER_PROPS.style,
    width: 960,
    maxWidth: "calc(100vw - 64px)",
    height: "min(760px, calc(100vh - 64px))",
  },
};
const CONTENT = { display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" };
// The playEngine export dialog's title and footer.
const TITLE = { textAlign: "center", color: "#eeeeee", fontSize: 20 };
const FOOTER = { display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8, padding: "4px 24px 20px 24px" };
const COLUMNS = {
  display: "grid",
  gridTemplateColumns: "minmax(0, 1fr) 380px",
  columnGap: 20,
  alignItems: "stretch",
  height: "100%",
  minHeight: 0,
};
// `minHeight: 0` or a child with its own scroll refuses to shrink and pushes
// the column past the dialog instead of scrolling inside it.
const COLUMN = { minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", overflowY: "auto" };
const BOX = { backgroundColor: CONTROL_BG, border: `1px solid ${CONTROL_BORDER}`, borderRadius: 4, overflow: "hidden" };
// A folder heads the rows beneath it, so it reads larger than them.
const ROW = { display: "flex", alignItems: "center", gap: 2, paddingRight: 10, cursor: "pointer" };
const ROW_NAME = { flex: 1, minWidth: 0, fontSize: 13, color: TEXT, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
const SMALL = { fontSize: 11, color: MUTED, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const FAILURES = { ...BOX, marginTop: 6, maxHeight: 132, overflowY: "auto", padding: "6px 10px", fontSize: 12, color: "#ef9a9a" };

// The playEngine export dialog's field label and credit row.
const FIELD_CAPTION = { fontSize: 14, color: "#9bcec0" };
/** "English - En", as the playEngine export dialog names a language. */
const languageName = (code) => LANGUAGE_CAPTIONS[String(code).toLowerCase()] || String(code).toUpperCase();
// The playEngine export's own input styling: without it MUI v4 draws these on
// its light palette and the text reads dim against the dark panel.
const FIELD_INPUT = {
  width: "100%",
  fontSize: "large",
  fontFamily: "Helvetica",
  color: "#eeeeee",
  marginTop: 16,
};
const fieldTheme = createTheme({
  palette: {
    type: "dark",
    primary: { main: "#4eabb1" },
    secondary: { main: "#4eabb1" },
  },
});
const CREDIT_ROW = { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", alignItems: "center", marginTop: 10 };
const CREDIT_LABEL = { fontSize: 13, color: "#9bcec0" };
const CREDIT_VALUE = { fontSize: 15, color: TEXT };
// The language select's "All" row; never a language code, so it cannot collide.
const EVERY_LANGUAGE = "__every__";
// Until the server says what the company's is.
const DEFAULT_TEMPLATE = "{versionName}_{languages}_{adNetworks}_{timeStamp}.zip";

const countOf = (count, one, many) => `${count} ${count === 1 ? one : many}`;
const STAGES = {
  build: "Writing the variants into the templates",
  package: "Naming and arranging the files",
  zip: "Zipping",
  upload: "Uploading",
};

function errorOf(result) {
  const error = result && result.error;
  if (!error) return `HTTP ${result && result.status}`;
  if (typeof error === "string") return error;
  if (error.error === "TOO_MANY_VARIANTS") return `one export takes at most ${error.max} variants`;
  if (error.error === "TOO_MANY_PLAYABLES") return `one export makes at most ${error.max} playables`;
  return error.error || JSON.stringify(error);
}

// Playin's own build, not an ad network's: the release ships it as the
// `default` template and the plan counts it as a test rather than a playable,
// exactly as the legacy export does (dialogVersionDownload.js).
const TEST_NETWORK = "default";

/** How much of a plan's allowance is left this period; Infinity when unknown. */
function remainingOf(account, allowance, spent) {
  if (!account || !account.plans || !account.getPlanIndex || !account.userCompany) return Infinity;
  const plan = account.plans[account.getPlanIndex(_.get(account, "userCompany.subscription.currentPlan"))];
  if (!plan) return Infinity;
  const periods = _.get(account, "userCompany.subscription.periods", []);
  const last = periods[periods.length - 1] || {};
  return (plan[allowance] || 0) - (last[spent] || 0);
}

/** How many playables the company's plan still allows this period; Infinity when unknown. */
export function remainingPlayables(account) {
  return remainingOf(account, "numberOfPlayables", "monthlyCreatedPlayables");
}

/** The same for test playables, which have their own allowance. */
export function remainingTests(account) {
  return remainingOf(account, "numberOfTests", "testPlayableCount");
}

/** Whether a listed variant has languages of its own (a release with the feature and more than "en"). */
const hasLanguagesItem = (item) => (item.languages || []).length > 1;

/**
 * @param props.initialIDs   the variants ticked when it opens
 * @param props.folderName   the folder it was opened on, which names the zip
 */
export default function DialogPe3dExport({ isOpen, onClose, editor, selectedBase, selectedGame, createSnackbar, account, initialIDs, folderName }) {
  const [ticked, setTicked] = useState([]);
  const [query, setQuery] = useState("");
  const [networks, setNetworks] = useState([]);
  // Languages (pe3dLocalization): those left unticked are not made; one file
  // ("all") opens in the device's language instead of a file per language.
  const [skippedLanguages, setSkippedLanguages] = useState([]);
  const [oneFile, setOneFile] = useState(false);
  const [name, setName] = useState("");
  const [isBuilding, setIsBuilding] = useState(false);
  const [progress, setProgress] = useState(null); // { stage, done, total }
  const [error, setError] = useState("");
  // A finished export that left some out: { file, made, failed }; the dialog stays open to say which.
  const [outcome, setOutcome] = useState(null);
  // The folders shown shut, by group key. A big base opens on the one folder
  // the dialog was asked for rather than on every variant it has.
  const [closedGroups, setClosedGroups] = useState([]);
  // The company's template and the tokens a template may use: { exportName, isCompanyOwn, tokens }.
  const [naming, setNaming] = useState(null);
  // The title the naming template writes, when it writes one. Editable here
  // for this export alone, as it is in the playEngine export dialog.
  const [gameTitleOverride, setGameTitleOverride] = useState("");
  // Submitting is its own action, confirmed each time: a download can be
  // thrown away, a file in the client's Dropbox cannot.
  const [isSubmitConfirmOpen, setIsSubmitConfirmOpen] = useState(false);
  // This export's own template; empty = the company's.
  // The folder the dialog opened on, brought into view once the list is drawn.
  const openedOn = useRef(null);
  const openedOnRow = useRef(null);
  // Set once the user works the folders themselves, so the tree stops deciding for them.
  const hasOpenedFolders = useRef(false);
  const [stamp, setStamp] = useState("");

  const baseID = selectedBase && selectedBase._id;
  useEffect(() => {
    if (!isOpen) return;
    setStamp(String(Date.now()));
    fetchExportNaming(baseID).then((result) => setNaming(result.ok ? result.data : null));
  }, [isOpen, baseID]);

  useEffect(() => {
    if (!isOpen) return;
    const known = new Set(editor.variants.map((item) => String(item._id)));
    setTicked((initialIDs || []).map(String).filter((id) => known.has(id)));
    setQuery("");
    setName(folderName || "");
    setError("");
    setProgress(null);
    setOutcome(null);
    setIsBuilding(false);
    setSkippedLanguages([]);
    setOneFile(false);
    // Filled rather than left on a placeholder, as the playEngine export
    // fills it: the field is an override, and what it overrides is what it
    // should open showing.
    setGameTitleOverride(_.get(selectedBase, "title", "") || _.get(selectedGame, "title", "") || "");
    setClosedGroups([]);
    openedOn.current = null;
    hasOpenedFolders.current = false;
  }, [isOpen]);

  /**
   * The library as the picker wants it: folders that nest, each with the
   * variants directly in it. `buildVariantTree` already nests; this only
   * renames its fields and counts what is under each folder.
   */
  const pickerTree = useMemo(() => {
    const convert = (nodes, prefix) => {
      const children = [];
      const items = [];
      for (const node of nodes) {
        if (node.type === "folder") {
          const path = `${prefix}${node.id}`;
          const folder = { path, name: node.title, ...convert(node.children, `${path}/`) };
          folder.count = folder.items.length + folder.children.reduce((sum, child) => sum + child.count, 0);
          if (folder.count) children.push(folder);
        } else {
          items.push({
            id: String(node.id),
            label: node.title,
            note: `${String(node.variant.game).split("/").pop()} ${node.variant.release} · ${hasLanguagesItem(node.variant) ? node.variant.languages.join(", ") : node.variant.language}`,
            variant: node.variant,
          });
        }
      }
      return { children, items };
    };
    const root = convert(buildVariantTree(editor.tree, editor.variants, query), "");
    root.path = "";
    root.name = "Top level";
    root.count = root.items.length + root.children.reduce((sum, child) => sum + child.count, 0);
    return root;
  }, [editor.tree, editor.variants, query]);

  /** Every folder in the tree, whatever the depth — the fold state is by path. */
  const allFolders = useMemo(() => {
    const collect = (node) => node.children.flatMap((child) => [child, ...collect(child)]);
    return collect(pickerTree);
  }, [pickerTree]);

  // Which folders start shut, worked out from the tree rather than when the
  // dialog opened: the variants and their tree arrive on their own, and a run
  // made before they did would read one top-level folder and shut nothing.
  // It stops as soon as the user opens or shuts one themselves.
  //
  // A folder the dialog was opened on stays open and everything else shuts. On
  // a dialog opened on nothing the named folders shut all the same — a base
  // with a folder per batch is the reason they fold at all — but the top level
  // stays open, since loose variants have no folder to open.
  useEffect(() => {
    if (!isOpen || hasOpenedFolders.current || !allFolders.length) return;
    const wanted = new Set((initialIDs || []).map(String));
    const holding = allFolders.filter((folder) => wanted.size > 0 && idsUnder(folder).some((id) => wanted.has(id)));
    if (wanted.size && !holding.length) return;
    // The folder the dialog was opened on stays open, with its parents, and
    // the rest fold. Opened on nothing, the named folders still fold — a base
    // with a folder per batch is why they fold at all.
    const open = new Set(holding.flatMap((folder) => folder.path.split("/").map((_part, index, parts) => parts.slice(0, index + 1).join("/"))));
    setClosedGroups(allFolders.filter((folder) => !open.has(folder.path)).map((folder) => folder.path));
    openedOn.current = holding.length ? [...wanted][0] : null;
  }, [isOpen, allFolders]);


  // Every variant in tree order, UNFILTERED: a search must never drop what is
  // already ticked from the count or from the export.
  const ordered = useMemo(
    () => variantGroups(buildVariantTree(editor.tree, editor.variants)).flatMap((group) => group.variants),
    [editor.tree, editor.variants]
  );
  const toggleGroupOpen = (path) => {
    hasOpenedFolders.current = true;
    setClosedGroups((current) => (current.includes(path) ? current.filter((item) => item !== path) : [...current, path]));
  };
  const tickedSet = useMemo(() => new Set(ticked), [ticked]);
  const involved = useMemo(() => ordered.filter((item) => tickedSet.has(String(item._id))), [ordered, tickedSet]);

  const networksOf = (item) => {
    const game = (editor.games || []).find((candidate) => candidate.package === item.game);
    const release = game && game.releases.find((candidate) => candidate.version === item.release);
    return (release && release.networks) || [];
  };
  // Every network a ticked variant's release has, and the ones they all have.
  const { known, common } = useMemo(() => {
    // Without the test build: that one is its own button on the preview
    // toolbar, as it is for playEngine, and the plan counts it apart.
    const lists = involved.map((item) => networksOf(item).filter((network) => network !== TEST_NETWORK));
    const all = _.uniq(lists.flat());
    const order = networkViews.map((view) => view.name);
    return {
      known: _.sortBy(all, (network) => (order.includes(network) ? order.indexOf(network) : order.length)),
      common: lists.length ? lists.reduce((kept, list) => kept.filter((network) => list.includes(network))) : [],
    };
  }, [involved, editor.games]);

  useEffect(() => {
    // Starts on AppLovin like the snapshot editor's export, when the releases have it.
    setNetworks((current) => {
      const kept = current.filter((network) => common.includes(network));
      if (kept.length || !common.length) return kept;
      return [common.includes("applovin") ? "applovin" : common[0]];
    });
  }, [common.join(",")]);

  useEffect(() => {
    if (!isOpen || !openedOn.current || !openedOnRow.current) return;
    openedOnRow.current.scrollIntoView({ block: "nearest" });
  }, [isOpen, closedGroups]);

  const productionKnown = known.filter((network) => network !== TEST_NETWORK);


  // The languages of the ticked variants that have more than one (a release without languages has its one label).
  const offeredLanguages = useMemo(() => _.uniq(involved.flatMap((item) => item.languages || ["en"])), [involved]);
  const hasLanguages = involved.some((item) => (item.languages || []).length > 1);
  const chosenLanguages = offeredLanguages.filter((language) => !skippedLanguages.includes(language));
  // What each variant is made in: the server narrows the request the same way (makeCreatives.js languagesOf).
  const languagesOfItem = (item) => {
    const own = item.languages || ["en"];
    if (own.length <= 1 && !hasLanguagesItem(item)) return [item.language || "en"];
    return oneFile ? ["all"] : own.filter((language) => chosenLanguages.includes(language));
  };
  const everyLanguage = offeredLanguages.length > 1 && chosenLanguages.length === offeredLanguages.length;

  // The "All" row is a toggle, not a language: picking it takes every language
  // on offer, picking it again goes back to one.
  function pickLanguages(picked) {
    if (!picked.includes(EVERY_LANGUAGE)) {
      setSkippedLanguages(offeredLanguages.filter((language) => !picked.includes(language)));
      return;
    }
    setSkippedLanguages(everyLanguage ? offeredLanguages.slice(1) : []);
  }

  /**
   * What the ticked variants weigh once exported — the snapshot editor's
   * estimate, applied to a variant's own files (server pe3d/sizeEstimate).
   * One call per variant, cached, and a variant whose answer is not in yet is
   * left out of the total rather than guessed at.
   */
  const [estimates, setEstimates] = useState({});
  useEffect(() => {
    if (!isOpen) return undefined;
    const missing = involved.map((item) => String(item._id)).filter((id) => !(id in estimates));
    if (!missing.length) return undefined;

    let live = true;
    Promise.all(
      missing.map((id) =>
        variantSizeEstimate(id)
          .then((result) => [id, result.ok ? _.get(result, "data.estimatedExportBytes", null) : null])
          .catch(() => [id, null])
      )
    ).then((pairs) => {
      if (live) setEstimates((current) => ({ ...current, ...Object.fromEntries(pairs) }));
    });
    return () => {
      live = false;
    };
  }, [isOpen, involved]);

  const isMany = involved.length > 1;
  // One file per ticked variant per language, before the networks multiply it.
  const variantBuilds = _.sum(involved.map((item) => languagesOfItem(item).length));
  const productionNetworks = networks.filter((network) => network !== TEST_NETWORK);
  const testNetworks = networks.filter((network) => network === TEST_NETWORK);
  const productionTotal = productionNetworks.length * variantBuilds;
  const testTotal = testNetworks.length * variantBuilds;
  const total = productionTotal + testTotal;
  const size = useMemo(() => {
    let bytes = 0;
    let known = 0;
    for (const item of involved) {
      const own = estimates[String(item._id)];
      if (!own) continue;
      bytes += own * languagesOfItem(item).length * networks.length;
      known += 1;
    }
    return { bytes, known, unknown: involved.length - known };
  }, [estimates, involved, networks, skippedLanguages, oneFile]);

  const remaining = remainingPlayables(account);
  const remainingTest = remainingTests(account);
  const limit =
    involved.length > MAX_VARIANTS
      ? `One export takes at most ${MAX_VARIANTS} variants; this one has ${involved.length}.`
      : total > MAX_PLAYABLES
        ? `One export makes at most ${MAX_PLAYABLES} playables; this one makes ${total}. Tick fewer variants or networks.`
        : productionTotal > remaining
          ? `Your plan allows ${Math.max(0, remaining)} more playables this period; this export makes ${productionTotal}.`
          : testTotal > remainingTest
            ? `Your plan allows ${Math.max(0, remainingTest)} more test playables this period; this export makes ${testTotal}.`
            : "";
  const companyTemplate = (naming && naming.exportName) || DEFAULT_TEMPLATE;
  const usesGameTitle = templateUsesGameTitle(companyTemplate);
  // A pe3d base is a shelf: its game only places it in the pickers, so the
  // name worth putting in a file name is the base's own. The game's title is
  // the fallback for a base without one.
  const gameTitle = (selectedBase && selectedBase.title) || (selectedGame && selectedGame.title) || "";
  const exportGameTitle = (usesGameTitle && gameTitleOverride.trim()) || gameTitle;
  // The names this export will give, in the order of the list.
  const fileNames = useMemo(
    () =>
      involved.map((item) =>
        exportFileName({
          own: "",
          companyTemplate,
          variant: { title: item.title, language: languagesOfItem(item).map((language) => (language === "all" ? "ML" : language)).join("_"), networks, gameTitle: exportGameTitle, baseTitle: (selectedBase && selectedBase.title) || "", timeStamp: stamp },
        })
      ),
    [involved, networks.join(","), companyTemplate, stamp, oneFile, skippedLanguages.join(","), exportGameTitle]
  );
  const shownNames = isMany ? folderNames(fileNames) : fileNames;


  const titleOf = (variantID) => (editor.variants.find((item) => String(item._id) === String(variantID)) || {}).title || "A variant";

  // The profile is on the company document, as the playEngine export reads it.
  const buildProfileData = _.get(account, "userCompany.buildProfileData");
  // One HTML that picks the device's language is sold per company.
  const supportsLangBundle = Boolean(_.get(account, "userCompany.bundleLanguage", false));
  const canDeliverDropbox = deliversToDropbox(buildProfileData);

  const run = async (toDropbox = false, dropboxFolder = "") => {
    // What is on screen is exported as it is on screen.
    if (editor.selectedID && tickedSet.has(String(editor.selectedID)) && editor.saveState !== "saved") {
      const saved = await editor.save();
      if (!saved) {
        setError(`“${titleOf(editor.selectedID)}” has unsaved changes that cannot be saved yet. Fix its problems first, or untick it.`);
        return;
      }
    }
    setIsBuilding(true);
    setError("");
    setOutcome(null);
    setProgress({ stage: "", done: 0, total: 0 });
    const ids = involved.map((item) => String(item._id));
    const typed = name.trim();
    const result = await exportVariants(
      {
        baseID: selectedBase._id,
        variantID: ids[0],
        ...(isMany ? { variantIDs: ids, folderName: typed } : {}),
        networks,
        ...(hasLanguages ? { languages: oneFile ? ["all"] : chosenLanguages } : {}),
        gameTitle: exportGameTitle,
        ...(toDropbox ? { deliverToDropbox: true, ...(dropboxFolder ? { dropboxFolder } : {}) } : {}),
      },
      {
        onProgress: (events) => {
          const last = events[events.length - 1] || {};
          setProgress({ stage: last.stage || "", done: last.done || 0, total: last.total || 0 });
        },
      }
    );
    setIsBuilding(false);
    setProgress(null);
    const failed = (result.ok ? result.data.failed : _.get(result, "error.failed")) || [];
    if (!result.ok || !result.data || !result.data.file) {
      setError(`Export failed: ${errorOf(result)}`);
      if (failed.length) setOutcome({ file: null, made: 0, failed });
      return;
    }
    const { file, exported = [] } = result.data;
    const madeIDs = new Set(exported.map((entry) => String(entry.variantID)));
    const madeVariantBuilds = _.sum(involved.filter((item) => madeIDs.has(String(item._id))).map((item) => languagesOfItem(item).length));
    // A build that failed for one network only cost that network's count.
    const lostOn = (list) => failed.filter((item) => list.includes(item.network) && exported.some((entry) => entry.variantID === item.variantID)).length;
    const madeProduction = madeVariantBuilds * productionNetworks.length - lostOn(productionNetworks);
    const madeTest = madeVariantBuilds * testNetworks.length - lostOn(testNetworks);
    const made = madeProduction + madeTest;
    // Playin's own build is counted against the plan's tests, the networks'
    // against its playables.
    const counters = {
      ...(madeProduction > 0 ? { monthlyCreatedPlayables: madeProduction } : {}),
      ...(madeTest > 0 ? { testPlayableCount: madeTest } : {}),
    };
    if (!_.isEmpty(counters) && account && account.userCompany) {
      incrementCompanyUsage(account.userCompany._id, counters)
        .then((company) => account.setUserCompany && account.setUserCompany(company))
        .catch((err) => console.log("createdPlayables update failed: " + (err && err.message)));
    }
    // The delivery reports itself through the export's warnings, so it reaches
    // the person even though this dialog closes a few lines down.
    for (const line of result.data.warnings || []) {
      if (String(line).startsWith("Dropbox")) createSnackbar(line, "info", false);
    }
    // A submitted export went to Dropbox; nothing should land in the person's
    // Downloads folder as well.
    if (!toDropbox) window.open(file);
    if (failed.length) {
      setOutcome({ file, made, failed });
      return;
    }
    createSnackbar(
      toDropbox
        ? `Submitted to Dropbox: ${countOf(made, "playable", "playables")}.`
        : `Export ready: ${countOf(made, "playable", "playables")}.`,
      "success",
      false
    );
    onClose();
  };

  const percent = progress && progress.total ? Math.min(100, Math.round((100 * progress.done) / progress.total)) : null;
  const canExport = !isBuilding && involved.length > 0 && networks.length > 0 && total > 0 && !limit;

  return (
    <Dialog open={Boolean(isOpen)} onClose={isBuilding ? undefined : onClose} maxWidth={false} PaperProps={PAPER_PROPS}>
      <DialogTitle style={TITLE}>EXPORT PE3D</DialogTitle>
      <DialogContent style={CONTENT}>
        <div style={COLUMNS}>
          {/* ── Which variants ── */}
          <div style={COLUMN}>
            <ExportPickerList
              caption="VARIANTS"
              tree={pickerTree}
              total={ordered.length}
              tickedIds={ticked}
              onTicked={setTicked}
              query={query}
              onQuery={setQuery}
              searchPlaceholder="Search variants"
              closedPaths={closedGroups}
              onToggleFolder={toggleGroupOpen}
              disabled={isBuilding}
              emptyText={ordered.length === 0 ? "This base has no variants yet." : "No variant matches."}
              itemRef={(id, node) => {
                if (id === openedOn.current) openedOnRow.current = node;
              }}
            />
          </div>

          {/* ── Which networks, and how ── */}
          {/* The fields are the playEngine export's: a title, a language
              selector and a network selector, in that order, so the two
              dialogs read the same. */}
          <div style={COLUMN}>
            <ThemeProvider theme={fieldTheme}>
              {usesGameTitle && (
                <FormControl fullWidth style={{ marginBottom: 14 }}>
                  <InputLabel shrink style={FIELD_CAPTION}>Game Title</InputLabel>
                  <Input
                    disabled={isBuilding}
                    placeholder={gameTitle}
                    value={gameTitleOverride}
                    onChange={(event) => setGameTitleOverride(event.target.value)}
                    style={FIELD_INPUT}
                  />
                </FormControl>
              )}

              {offeredLanguages.length > 0 && (
                <>
                  <FormControl fullWidth style={{ marginBottom: 6 }}>
                    <InputLabel shrink style={FIELD_CAPTION}>Select Language</InputLabel>
                    <Select
                      multiple
                      disabled={isBuilding || oneFile}
                      value={chosenLanguages}
                      style={FIELD_INPUT}
                      onChange={(event) => pickLanguages(event.target.value)}
                      renderValue={() => {
                        if (oneFile) return "All languages in one HTML";
                        if (everyLanguage) return "All";
                        return chosenLanguages.map(languageName).join(", ");
                      }}
                    >
                      {offeredLanguages.length > 1 && (
                        <MenuItem value={EVERY_LANGUAGE}>
                          <Checkbox size="small" checked={everyLanguage} />
                          <ListItemText primary="All" />
                        </MenuItem>
                      )}
                      {offeredLanguages.map((language) => (
                        <MenuItem key={language} value={language}>
                          <Checkbox size="small" checked={chosenLanguages.includes(language)} />
                          <ListItemText primary={languageName(language)} />
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  {/* A company setting, as in the playEngine export: not
                      every company buys the one-file build. */}
                  {supportsLangBundle && (
                    <span style={{ ...ROW, cursor: "pointer", marginBottom: 10 }} onClick={() => !isBuilding && setOneFile((value) => !value)}>
                      <Checkbox size="small" color="primary" checked={oneFile} disabled={isBuilding} />
                      <span style={ROW_NAME}>Bundle all languages into one HTML</span>
                    </span>
                  )}
                </>
              )}

              <FormControl fullWidth style={{ marginBottom: 14 }}>
                <InputLabel shrink style={FIELD_CAPTION}>Select Network</InputLabel>
                <Select
                  multiple
                  disabled={isBuilding || involved.length === 0}
                  value={networks.filter((network) => productionKnown.includes(network))}
                  style={FIELD_INPUT}
                  onChange={(event) => setNetworks(event.target.value)}
                  renderValue={(value) => value.map(networkCaption).join(", ")}
                >
                  {productionKnown.map((network) => (
                    <MenuItem key={network} value={network} disabled={!common.includes(network)}>
                      <Checkbox size="small" checked={networks.includes(network)} />
                      <ListItemText
                        primary={networkCaption(network)}
                        secondary={common.includes(network) ? undefined : "not in every release"}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </ThemeProvider>

            {involved.length > 0 && (
              <div style={{ marginTop: 4 }}>
                <div style={DIALOG_CAPTION}>
                  <span>GOING OUT</span>
                </div>
                <div style={{ maxHeight: 160, overflowY: "auto" }} className="proto-dark-scroll">
                  {involved.map((item, index) => (
                    <div key={String(item._id)} style={{ ...ROW_NAME, lineHeight: 1.7, fontSize: 12, color: "#cfd4da" }} title={item.title}>
                      {shownNames[index] || item.title}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {size.bytes > 0 && (
              <Typography style={{ ...DIALOG_TEXT.headline, marginTop: 10 }}>
                {`About ~${formatBytes(size.bytes)}${total > 1 ? " across all builds" : ""}.`}
                {size.unknown > 0 ? ` ${size.unknown} not estimated yet.` : ""}
              </Typography>
            )}

            {/* The plan's three numbers, as the playEngine export shows them. */}
            <div style={CREDIT_ROW}>
              <span style={{ ...CREDIT_LABEL, textAlign: "left" }}>Current Credit</span>
              <span style={{ ...CREDIT_LABEL, textAlign: "center" }}>Html Count</span>
              <span style={{ ...CREDIT_LABEL, textAlign: "right" }}>Credit Left</span>
            </div>
            <div style={CREDIT_ROW}>
              <span style={{ ...CREDIT_VALUE, textAlign: "left" }}>{Number.isFinite(remaining) ? remaining : "—"}</span>
              <span style={{ ...CREDIT_VALUE, textAlign: "center" }}>{total}</span>
              <span style={{ ...CREDIT_VALUE, textAlign: "right" }}>{Number.isFinite(remaining) ? Math.max(0, remaining - productionTotal) : "—"}</span>
            </div>

            {shownNames.length > 0 && (
              <Typography style={DIALOG_TEXT.hint}>Named by the company&apos;s convention.</Typography>
            )}
          </div>
        </div>

        {limit && <Typography style={DIALOG_TEXT.warning}>{limit}</Typography>}
        {isBuilding && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, marginBottom: 6 }}>
              <CircularProgress size={16} />
              <span>{(progress && STAGES[progress.stage]) || "Starting"}…</span>
              {percent !== null && (
                <span style={{ ...SMALL, marginLeft: "auto", fontVariantNumeric: "tabular-nums" }}>
                  {progress.done} of {progress.total}
                </span>
              )}
            </div>
            <LinearProgress variant={percent === null ? "indeterminate" : "determinate"} value={percent === null ? undefined : percent} />
            <span style={DIALOG_TEXT.waitLine}>A large export takes a few minutes.</span>
          </div>
        )}
        {error && <Typography style={DIALOG_TEXT.failure}>{error}</Typography>}
        {outcome && (
          <>
            {outcome.file && (
              <Typography style={{ ...DIALOG_TEXT.headline, fontSize: 13 }}>
                {countOf(outcome.made, "playable", "playables")} exported.{" "}
                <a href={outcome.file} target="_blank" rel="noopener noreferrer" style={{ color: "#4eabb1" }}>
                  Download again
                </a>
              </Typography>
            )}
            <Typography style={DIALOG_TEXT.failure}>{outcome.failed.length} could not be made:</Typography>
            <div style={FAILURES} className="proto-dark-scroll">
              {outcome.failed.map((item, index) => (
                <div key={index}>
                  <b>{titleOf(item.variantID)}</b>
                  {item.network ? ` · ${networkCaption(item.network)}` : ""}: {[item.error, ...(item.problems || [])].filter(Boolean).join(" · ")}
                </div>
              ))}
            </div>
          </>
        )}
      </DialogContent>
      {/* The playEngine export's footer, button for button (dialogPlayEngineExport.js). */}
      <DialogActions style={FOOTER}>
        <Button onClick={onClose} disabled={isBuilding} style={{ color: "#bbb" }}>
          Close
        </Button>
        {canDeliverDropbox && (
          <Button
            variant="outlined"
            size="small"
            disabled={!canExport}
            onClick={() => setIsSubmitConfirmOpen(true)}
            style={{ color: "#9bcec0", borderColor: "#4a6d68", textTransform: "none" }}
          >
            Submit to Dropbox
          </Button>
        )}
        <Button variant="contained" color="primary" disabled={!canExport} onClick={() => run()}>
          {isBuilding ? (
            <CircularProgress size={16} color="inherit" />
          ) : (
            <>
              <DownloadIcon style={{ width: 18, height: 18, marginRight: 6 }} />
              Download
            </>
          )}
        </Button>
      </DialogActions>

      <DialogDropboxSubmit
        isOpen={isSubmitConfirmOpen}
        defaultCompanyFolder={_.get(account, "userCompany.name")}
        defaultGameFolder={gameTitle}
        gameCode={exportGameTitle}
        destinationFor={(folder) =>
          dropboxDestination(buildProfileData, {
            dropboxFolder: folder,
            gameTitle: exportGameTitle,
            versionName: String(_.get(involved, [0, "title"], "")).replace(/ /g, "_"),
          })
        }
        onCancel={() => setIsSubmitConfirmOpen(false)}
        onConfirm={(folder) => {
          setIsSubmitConfirmOpen(false);
          void run(true, folder);
        }}
      />
    </Dialog>
  );
}
